import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, PaperPlaneTilt, Megaphone, ChatCircleText, CaretLeft, User, PencilSimple, Trash } from "@phosphor-icons/react";

const MGMT_ROLES = ["owner"];
function ts(iso) { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Pacific/Honolulu" }); }

function Composer({ placeholder, testid, value, onChange, onSend, busy }) {
  return (
    <div className="sticky bottom-0 bg-white border-t-2 border-black p-3 flex gap-2">
      <input data-testid={`${testid}-input`} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSend()} placeholder={placeholder}
        className="flex-1 h-11 px-3 rounded-full border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" />
      <button data-testid={`${testid}-send-btn`} onClick={onSend} disabled={busy || !value.trim()} className="press w-11 h-11 rounded-full bg-primary text-white border-2 border-black flex items-center justify-center disabled:opacity-50"><PaperPlaneTilt size={18} weight="fill" /></button>
    </div>
  );
}

function Bubble({ m, meId, canManage, onEdit, onDelete }) {
  const mine = m.sender_id === meId;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(m.body);
  useEffect(() => { setVal(m.body); }, [m.body]);
  const save = () => { const t = val.trim(); if (t && t !== m.body) onEdit(m.id, t); setEditing(false); };
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`comm-msg-${m.id}`}>
      <div className={`group max-w-[80%] rounded-2xl border-2 border-black px-3 py-2 ${mine ? "bg-primary text-white" : m.sender_is_mgmt ? "bg-[#E0F2FE]" : "bg-white"}`}>
        {!mine && <p className="text-[10px] font-bold uppercase tracking-wide opacity-70">{m.sender_name}</p>}
        {editing ? (
          <div className="flex flex-col gap-1">
            <input data-testid={`comm-edit-input-${m.id}`} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
              className="text-sm text-black rounded-lg border-2 border-black px-2 py-1 outline-none" autoFocus />
            <div className="flex gap-2 justify-end">
              <button data-testid={`comm-edit-cancel-${m.id}`} onClick={() => { setEditing(false); setVal(m.body); }} className="text-[11px] font-bold underline opacity-80">Cancel</button>
              <button data-testid={`comm-edit-save-${m.id}`} onClick={save} className="text-[11px] font-bold underline">Save</button>
            </div>
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <p className={`text-[10px] ${mine ? "text-white/70" : "text-black/40"}`}>{ts(m.created_at)}{m.edited_at ? " · edited" : ""}</p>
          {canManage && !editing && (
            <span className="ml-auto flex items-center gap-2">
              <button data-testid={`comm-edit-btn-${m.id}`} onClick={() => setEditing(true)} className={`${mine ? "text-white/80" : "text-black/50"} hover:opacity-100`}><PencilSimple size={13} weight="bold" /></button>
              <button data-testid={`comm-delete-btn-${m.id}`} onClick={() => onDelete(m.id)} className={`${mine ? "text-white/80" : "text-black/50"} hover:opacity-100`}><Trash size={13} weight="bold" /></button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommNotesPanel({ open, onClose }) {
  const { user } = useAuth();
  const mgmt = MGMT_ROLES.includes(user?.role);
  const [tab, setTab] = useState("direct"); // mgmt: direct | broadcast ; employee: uses direct list only
  const [threads, setThreads] = useState([]);
  const [sel, setSel] = useState(null); // {employee_id, employee_name} for mgmt
  const [direct, setDirect] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadThreads = useCallback(async () => {
    if (!mgmt) return;
    try { const { data } = await api.get("/comm/threads"); setThreads(data); } catch {}
  }, [mgmt]);

  const loadMessages = useCallback(async (employeeId) => {
    const params = employeeId ? { employee_id: employeeId } : {};
    try { const { data } = await api.get("/comm/messages", { params }); setDirect(data.direct || []); setBroadcasts(data.broadcasts || []); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (mgmt) { loadThreads(); if (tab === "broadcast") loadMessages(null); }
    else loadMessages(null);
  }, [open, mgmt, tab, loadThreads, loadMessages]);

  useEffect(() => { if (sel) loadMessages(sel.employee_id); }, [sel, loadMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [direct, broadcasts, sel, tab]);

  if (!open) return null;

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    const payload = mgmt
      ? (tab === "broadcast" ? { body, scope: "broadcast" } : { body, scope: "direct", employee_id: sel.employee_id })
      : { body, scope: "direct" };
    try {
      await api.post("/comm/messages", payload);
      setText("");
      if (mgmt && tab === "broadcast") await loadMessages(null);
      else if (mgmt) { await loadMessages(sel.employee_id); loadThreads(); }
      else await loadMessages(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const canManage = (m) => m.sender_id === user.id || user.role === "owner";
  const reload = () => {
    if (mgmt && tab === "broadcast") loadMessages(null);
    else if (mgmt && sel) { loadMessages(sel.employee_id); loadThreads(); }
    else loadMessages(null);
  };
  const editMsg = async (id, body) => { try { await api.put(`/comm/messages/${id}`, { body }); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const deleteMsg = async (id) => { try { await api.delete(`/comm/messages/${id}`); toast.success("Message deleted"); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="comm-notes-panel" onClick={(e) => e.stopPropagation()} className="relative bg-background border-l-2 border-black w-full max-w-lg h-full flex flex-col">
        <div className="sticky top-0 bg-[#E0F2FE] border-b-2 border-black px-5 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><ChatCircleText size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">{mgmt ? "Team Messages" : "Message Management"}</h2>
          </div>
          <button data-testid="comm-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
        </div>

        {/* ---- Management view ---- */}
        {mgmt ? (
          <>
            <div className="flex gap-2 p-3 border-b-2 border-black shrink-0">
              <button data-testid="comm-tab-direct" onClick={() => { setTab("direct"); }} className={`px-3 h-9 rounded-full border-2 border-black text-xs font-bold ${tab === "direct" ? "bg-primary text-white" : "bg-white"}`}>Direct</button>
              <button data-testid="comm-tab-broadcast" onClick={() => { setTab("broadcast"); setSel(null); }} className={`px-3 h-9 rounded-full border-2 border-black text-xs font-bold flex items-center gap-1 ${tab === "broadcast" ? "bg-primary text-white" : "bg-white"}`}><Megaphone size={13} weight="bold" /> Announcements</button>
            </div>

            {tab === "broadcast" ? (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  <p className="text-xs text-black/50">Announcements are sent to all employees.</p>
                  {broadcasts.length === 0 && <p className="text-sm text-black/40 mt-4 text-center">No announcements yet.</p>}
                  {broadcasts.map((m) => <Bubble key={m.id} m={m} meId={user.id} canManage={canManage(m)} onEdit={editMsg} onDelete={deleteMsg} />)}
                  <div ref={endRef} />
                </div>
                <Composer placeholder="Announce to all employees…" testid="comm-broadcast" value={text} onChange={setText} onSend={send} busy={busy} />
              </>
            ) : sel ? (
              <>
                <div className="flex items-center gap-2 p-3 border-b border-black/10 shrink-0">
                  <button data-testid="comm-back-btn" onClick={() => setSel(null)} className="press w-8 h-8 rounded-full border-2 border-black flex items-center justify-center bg-white"><CaretLeft size={15} weight="bold" /></button>
                  <p className="font-bold flex items-center gap-1"><User size={15} /> {sel.employee_name}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {direct.length === 0 && <p className="text-sm text-black/40 mt-4 text-center">No messages yet. Start the conversation.</p>}
                  {direct.map((m) => <Bubble key={m.id} m={m} meId={user.id} canManage={canManage(m)} onEdit={editMsg} onDelete={deleteMsg} />)}
                  <div ref={endRef} />
                </div>
                <Composer placeholder={`Message ${sel.employee_name}…`} testid="comm-direct" value={text} onChange={setText} onSend={send} busy={busy} />
              </>
            ) : (
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {threads.length === 0 && <p className="text-sm text-black/40 mt-4 text-center">No employees.</p>}
                {threads.map((t) => (
                  <button key={t.employee_id} data-testid={`comm-thread-${t.employee_id}`} onClick={() => setSel({ employee_id: t.employee_id, employee_name: t.employee_name })}
                    className="press w-full text-left rounded-xl border-2 border-black bg-white p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-sm flex items-center gap-1"><User size={13} /> {t.employee_name}</p>
                      <p className="text-xs text-black/50 truncate">{t.last_message || "No messages yet"}</p>
                    </div>
                    {t.unread > 0 && <span data-testid={`comm-unread-${t.employee_id}`} className="shrink-0 text-[10px] font-bold px-2 py-1 rounded-full bg-[#FF3B30] text-white border-2 border-black">{t.unread}</span>}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          /* ---- Employee view ---- */
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {broadcasts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-black/40 flex items-center gap-1"><Megaphone size={13} weight="bold" /> Announcements</p>
                  {broadcasts.map((m) => (
                    <div key={m.id} data-testid={`comm-broadcast-${m.id}`} className="rounded-xl border-2 border-black bg-[#FEF9C3] p-3">
                      <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                      <p className="text-[10px] text-black/40 mt-1">{m.sender_name} · {ts(m.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-black/40 pt-1">Your messages with management</p>
              {direct.length === 0 && <p className="text-sm text-black/40 text-center py-4">Send a note to your managers below.</p>}
              {direct.map((m) => <Bubble key={m.id} m={m} meId={user.id} canManage={canManage(m)} onEdit={editMsg} onDelete={deleteMsg} />)}
              <div ref={endRef} />
            </div>
            <Composer placeholder="Write a note to management…" testid="comm-emp" value={text} onChange={setText} onSend={send} busy={busy} />
          </>
        )}
      </div>
    </div>
  );
}
